class ImageGalleryModal extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.overlay = null;
    this.modal = null;
  }

  connectedCallback() {
    this.createOverlay();
    this.createModal();
    this.renderGallery();
    this.renderModal();
    this.setupEventListeners();
  }

  disconnectedCallback() {
    if (this.overlay) {
      this.overlay.remove();
    }
    if (this.modal) {
      this.modal.remove();
    }
  }

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'igm-overlay';
    document.body.appendChild(this.overlay);
  }

  createModal() {
    this.modal = document.createElement('div');
    this.modal.className = 'igm-modal';
    this.modal.id = 'imageModal';
    document.body.appendChild(this.modal);
  }

  static get observedAttributes() {
    return ['images'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'images' && oldValue !== newValue) {
      this.renderGallery();
      this.renderModal();
      this.setupEventListeners();
    }
  }

  renderGallery() {
    const images = this.getImages();
    this.innerHTML = `
            <div class="igm-gallery">
                ${images.map((img, index) => `
                    <div class="igm-gallery-item">
                        <img src="${img.src}" alt="${img.alt || `Gallery Image ${index + 1}`}">
                    </div>
                `).join('')}
            </div>
        `;
  }

  renderModal() {
    const images = this.getImages();
    if (this.modal) {
      this.modal.innerHTML = `
                <div class="igm-modal-content">
                    <span class="igm-close-modal">&times;</span>
                    <div class="igm-slider-container">
                        <button class="igm-slider-arrow igm-prev">&lt;</button>
                        <div class="igm-slider">
                            <img src="" alt="Slider Image" id="modalImage">
                        </div>
                        <button class="igm-slider-arrow igm-next">&gt;</button>
                    </div>
                    <div class="igm-slider-info">
                        <span id="currentSlide">1</span> / <span id="totalSlides">${images.length}</span>
                    </div>
                </div>
            `;
    }
  }

  getImages() {
    const imagesAttr = this.getAttribute('images');
    if (imagesAttr) {
      try {
        return JSON.parse(imagesAttr);
      } catch (e) {
        console.error('Invalid images attribute format');
        return [];
      }
    }
    return [];
  }

  setupEventListeners() {
    // Get all required elements
    const modalImg = this.modal?.querySelector('#modalImage');
    const closeBtn = this.modal?.querySelector('.igm-close-modal');
    const prevBtn = this.modal?.querySelector('.igm-prev');
    const nextBtn = this.modal?.querySelector('.igm-next');
    const currentSlideSpan = this.modal?.querySelector('#currentSlide');
    const galleryItems = this.querySelectorAll('.igm-gallery-item img');
    const images = this.getImages();

    // Check if all required elements exist
    if (!modalImg || !closeBtn || !prevBtn || !nextBtn || !currentSlideSpan) {
      console.error('Some modal elements are missing');
      return;
    }

    // Gallery click handlers
    galleryItems.forEach((img, index) => {
      img.addEventListener('click', () => {
        this.currentIndex = index;
        this.openModal();
      });
    });

    // Modal close handlers
    closeBtn.addEventListener('click', () => {
      this.closeModal();
    });

    if (this.overlay) {
      this.overlay.addEventListener('click', () => {
        this.closeModal();
      });
    }

    // Navigation handlers
    prevBtn.addEventListener('click', () => {
      this.currentIndex = (this.currentIndex - 1 + images.length) % images.length;
      this.updateModalImage();
      this.dispatchEvent(new CustomEvent('slide-change', {
        detail: { index: this.currentIndex }
      }));
    });

    nextBtn.addEventListener('click', () => {
      this.currentIndex = (this.currentIndex + 1) % images.length;
      this.updateModalImage();
      this.dispatchEvent(new CustomEvent('slide-change', {
        detail: { index: this.currentIndex }
      }));
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (this.modal?.style.display === 'block') {
        if (e.key === 'ArrowLeft') {
          prevBtn.click();
        } else if (e.key === 'ArrowRight') {
          nextBtn.click();
        } else if (e.key === 'Escape') {
          this.closeModal();
        }
      }
    });
  }

  updateModalImage() {
    if (!this.modal) return;

    const modalImg = this.modal.querySelector('#modalImage');
    const currentSlideSpan = this.modal.querySelector('#currentSlide');
    const images = this.getImages();

    if (modalImg && currentSlideSpan && images[this.currentIndex]) {
      modalImg.src = images[this.currentIndex].src;
      currentSlideSpan.textContent = this.currentIndex + 1;
    }
  }

  openModal() {
    if (!this.modal || !this.overlay) return;

    this.modal.style.display = 'block';
    this.overlay.style.display = 'block';
    document.body.style.overflow = 'hidden';
    this.updateModalImage();
    this.dispatchEvent(new CustomEvent('modal-open', {
      detail: { index: this.currentIndex }
    }));
  }

  closeModal() {
    if (!this.modal || !this.overlay) return;

    this.modal.style.display = 'none';
    this.overlay.style.display = 'none';
    document.body.style.overflow = 'auto';
    this.dispatchEvent(new CustomEvent('modal-close'));
  }
}

customElements.define('image-gallery-modal', ImageGalleryModal);